import React from "react";
import Home from "../Home/Home";
import { connect } from "react-redux";
import {
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Chip,
  Tooltip,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import LinkIcon from "@material-ui/icons/Link";
import StarIcon from "@material-ui/icons/Star";
import HelpIcon from "@material-ui/icons/Help";
import { Storage } from "aws-amplify";
import { v4 as uuid } from "uuid";
import { localStoragePut } from "../../util/localStorage";

import {
  getAvatars,
  createAvatar,
  syncAvatar,
  requestAvatarConsent,
  setDefaultAvatar,
  deleteAvatar,
} from "../../Redux/Actions/agent";
import { selectAvatars } from "../../Redux/Selectors";
import "./style.css";

const isLocalAuth = process.env.REACT_APP_LOCAL_AUTH === "true";
const S3_BUCKET = process.env.REACT_APP_S3_BUCKET || "";
const S3_REGION = process.env.REACT_APP_S3_REGION || "us-east-1";

interface IProps {
  avatars: any[];
  loading: boolean;
  saving: boolean;
  getAvatars: () => void;
  createAvatar: (payload: any) => void;
  syncAvatar: (id: string) => void;
  requestAvatarConsent: (id: string) => void;
  setDefaultAvatar: (id: string) => void;
  deleteAvatar: (id: string) => void;
}

const STATUS_LABEL: any = {
  processing: { label: "Training", color: "#ed6c02" },
  pending_consent: { label: "Consent needed", color: "#ed6c02" },
  completed: { label: "Ready", color: "#2e7d32" },
  failed: { label: "Failed", color: "#d32f2f" },
};

class Avatars extends React.Component<IProps> {
  state = {
    type: "photo",
    name: "",
    fileUrl: "",
    prompt: "",
    uploading: false,
  };

  componentDidMount() {
    this.props.getAvatars();
  }

  uploadFile = async (file: File) => {
    if (!file) return;
    this.setState({ uploading: true });
    try {
      const extension = file.name.split(".").pop() || "bin";
      const key = `avatar-${uuid()}.${extension}`;
      if (isLocalAuth) {
        const result = await localStoragePut(key, file, { level: "public" });
        const localKey = result?.key || key;
        this.setState({
          fileUrl: `${window.location.origin}/uploads/public/${localKey}`,
          uploading: false,
        });
      } else {
        const result = await Storage.put(key, file, { level: "public", contentType: file.type });
        const storageKey = result?.key || key;
        this.setState({
          fileUrl: `https://${S3_BUCKET}.s3.${S3_REGION}.amazonaws.com/public/${storageKey}`,
          uploading: false,
        });
      }
    } catch (error: any) {
      this.setState({ uploading: false });
      alert(`Upload failed: ${error.message || "unknown error"}`);
    }
  };

  create = () => {
    const { type, name, fileUrl, prompt } = this.state;
    if (!name.trim()) return;
    if (type === "prompt") {
      if (!prompt.trim()) return;
      this.props.createAvatar({ type, name, prompt });
    } else {
      if (!fileUrl) return;
      this.props.createAvatar({ type, name, fileUrl });
    }
    this.setState({ name: "", fileUrl: "", prompt: "" });
  };

  copyConsent = (url: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
    }
  };

  renderAvatar = (avatar: any) => {
    const status = STATUS_LABEL[avatar.status] || STATUS_LABEL.processing;
    return (
      <div className="avatarCard" key={avatar.id}>
        <div className="avatarCardTop">
          {avatar.previewImageUrl ? (
            <img className="avatarThumb" src={avatar.previewImageUrl} alt={avatar.name} />
          ) : (
            <div className="avatarThumb avatarThumbEmpty" />
          )}
          <div className="avatarInfo">
            <div className="avatarName">
              {avatar.name}
              {avatar.isDefault && (
                <Tooltip title="Default avatar" arrow>
                  <StarIcon fontSize="small" className="avatarDefaultIcon" />
                </Tooltip>
              )}
            </div>
            <div className="avatarMeta">
              {avatar.avatarType === "digital_twin"
                ? "Digital twin"
                : avatar.avatarType === "photo"
                ? "Photo avatar"
                : "Prompt avatar"}
            </div>
            <Chip
              size="small"
              label={status.label}
              style={{ backgroundColor: status.color, color: "#fff", marginTop: 6 }}
            />
            {avatar.error && <div className="avatarError">{avatar.error}</div>}
          </div>
        </div>
        <div className="avatarActions">
          <Button
            size="small"
            startIcon={<RefreshIcon fontSize="small" />}
            onClick={() => this.props.syncAvatar(avatar.id)}
          >
            Refresh status
          </Button>
          {avatar.status === "pending_consent" && (
            <Button
              size="small"
              startIcon={<LinkIcon fontSize="small" />}
              onClick={() => {
                if (avatar.consentUrl) {
                  this.copyConsent(avatar.consentUrl);
                } else {
                  this.props.requestAvatarConsent(avatar.id);
                }
              }}
            >
              {avatar.consentUrl ? "Copy consent link" : "Create consent link"}
            </Button>
          )}
          {avatar.status === "completed" && !avatar.isDefault && (
            <Button
              size="small"
              startIcon={<CheckCircleIcon fontSize="small" />}
              onClick={() => this.props.setDefaultAvatar(avatar.id)}
            >
              Make default
            </Button>
          )}
          <Button
            size="small"
            color="secondary"
            startIcon={<DeleteIcon fontSize="small" />}
            onClick={() => this.props.deleteAvatar(avatar.id)}
          >
            Remove
          </Button>
        </div>
        {avatar.consentUrl && (
          <div className="avatarConsentUrl">
            Send this link to the person on camera (valid 24 hours): {avatar.consentUrl}
          </div>
        )}
      </div>
    );
  };

  render() {
    const { avatars, loading, saving } = this.props;
    const { type, name, fileUrl, prompt, uploading } = this.state;

    return (
      <Home>
        <div className="agentWrapper">
          <div className="agentHeader">
            <div>
              <span className="heading">Video Avatars </span>
              <span className="headingTooltip">
                <Tooltip
                  title="Avatars are created on Viideon's HeyGen account. Digital twins need a one-time consent recording from the person on camera."
                  placement="right-start"
                  arrow
                >
                  <HelpIcon />
                </Tooltip>
              </span>
              <div className="agentSub">
                {avatars.length} avatar{avatars.length === 1 ? "" : "s"}
              </div>
            </div>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={this.props.getAvatars}>
              Refresh
            </Button>
          </div>

          <div className="agentCard">
            <div className="agentCardTitle">Create an avatar</div>
            <div className="agentCardHint">
              Photo avatars are ready in minutes without consent. Digital twins look and sound like
              the real person but require a recorded consent from them.
            </div>
            <div className="avatarForm">
              <TextField
                select
                label="Type"
                value={type}
                onChange={(event) => this.setState({ type: event.target.value })}
                variant="outlined"
                size="small"
                className="avatarFormType"
              >
                <MenuItem value="photo">Photo avatar</MenuItem>
                <MenuItem value="digital_twin">Digital twin (video)</MenuItem>
                <MenuItem value="prompt">AI character (no real person)</MenuItem>
              </TextField>
              <TextField
                label="Name"
                value={name}
                onChange={(event) => this.setState({ name: event.target.value })}
                variant="outlined"
                size="small"
                className="avatarFormName"
              />
              {type === "prompt" ? (
                <TextField
                  label="Describe the character"
                  value={prompt}
                  onChange={(event) => this.setState({ prompt: event.target.value })}
                  variant="outlined"
                  size="small"
                  className="avatarFormPrompt"
                />
              ) : (
                <div className="avatarFileRow">
                  <input
                    id="avatar-file"
                    type="file"
                    accept={type === "photo" ? "image/*" : "video/mp4,video/*"}
                    style={{ display: "none" }}
                    onChange={(event) => {
                      const file = event.target.files && event.target.files[0];
                      if (file) this.uploadFile(file);
                    }}
                  />
                  <label htmlFor="avatar-file">
                    <Button variant="outlined" component="span" disabled={uploading}>
                      {uploading ? "Uploading..." : type === "photo" ? "Choose photo" : "Choose video"}
                    </Button>
                  </label>
                  {fileUrl && <span className="avatarFileOk">Uploaded</span>}
                </div>
              )}
              <Button
                variant="contained"
                className="agentPrimaryButton"
                onClick={this.create}
                disabled={saving || uploading || !name.trim()}
              >
                {saving ? "Creating..." : "Create avatar"}
              </Button>
            </div>
          </div>

          {loading && avatars.length === 0 ? (
            <div className="agentLoading">
              <CircularProgress />
            </div>
          ) : avatars.length === 0 ? (
            <div className="inboxEmptyText">No avatars yet.</div>
          ) : (
            <div className="avatarsGrid">{avatars.map(this.renderAvatar)}</div>
          )}
        </div>
      </Home>
    );
  }
}

const mapStateToProps = (state: any) => ({
  avatars: selectAvatars(state),
  loading: state?.agent?.loading,
  saving: state?.agent?.saving,
});

export default connect(mapStateToProps, {
  getAvatars,
  createAvatar,
  syncAvatar,
  requestAvatarConsent,
  setDefaultAvatar,
  deleteAvatar,
})(Avatars);
